/**
 * Schritt 3: Einzel-Simulation
 * Fokussiert auf einzelne 3-Topf und 1-Topf Portfolio-Simulationen
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
} from '@cloudscape-design/components';

import { AppZustand, SimulationOptions, SteuerParameter } from '@/types';
import { CrisisDefinitionType } from '@/types/simulation';

import { SimpleSimulationControls } from '../components/simulation/SimpleSimulationControls';
import { EinzelSimulationTab } from '../components/simulation/EinzelSimulationTab';
// GARCHUtils is used in DataExportService, not directly here
import { ErrorHandler } from '../services/ErrorHandler';
import { RateCalculationService } from '../services/RateCalculationService';
import { SharedRateGenerator } from '../services/SharedRateGenerator';
import { garchSequenceCache } from '../services/CacheManager';
import { ThreeTopfSimulationEngine } from '../services/ThreeTopfSimulationEngine';
import { OneTopfSimulationEngine } from '../services/OneTopfSimulationEngine';
import { DataExportService, ExportConfig } from '../services/DataExportService';
import { ExcelFormattingOptions, FormattedExcelExportService } from '../services/FormattedExcelExportService';

interface Schritt3Props {
  steuerParameter: SteuerParameter | null;
  simulationOptions: SimulationOptions;
  crisisDefinition: string;
  onSimulationOptionsUpdate: (options: Partial<SimulationOptions>) => void;
  onCrisisDefinitionUpdate: (crisisDefinition: string) => void;
  simulationState: AppZustand['simulationState'];
  onSimulationStateUpdate: (updates: Partial<AppZustand['simulationState']>) => void;
}

/**
 * Komponente für die Einzel-Simulation (3-Topf und 1-Topf)
 */
const Schritt3: React.FC<Schritt3Props> = ({
  steuerParameter,
  simulationOptions,
  crisisDefinition,
  onSimulationOptionsUpdate,
  onCrisisDefinitionUpdate,
  simulationState,
  onSimulationStateUpdate,
}) => {
  // Extract options from centralized state
  const { inflationOption, investmentReturnOption, tagesgeldOption, zinstopfOption } =
    simulationOptions;

  // Create handlers that update centralized state
  const setCrisisDefinition = (value: string) => onCrisisDefinitionUpdate(value);
  const setInflationOption = (value: string) =>
    onSimulationOptionsUpdate({ inflationOption: value });
  const setInvestmentReturnOption = (value: string) =>
    onSimulationOptionsUpdate({ investmentReturnOption: value });
  const setTagesgeldOption = (value: string) =>
    onSimulationOptionsUpdate({ tagesgeldOption: value });
  const setZinstopfOption = (value: string) => onSimulationOptionsUpdate({ zinstopfOption: value });

  // Extract simulation state from global state
  const {
    simulationData,
    isSimulating,
    simulationError,
    oneTopfSimulationData,
    isRunning1TopfSimulation,
    oneTopfSimulationError,
  } = simulationState;


  const runAllSimulations = async () => {
    if (!steuerParameter) {return;}

    // Parameter vor dem Simulationsstart validieren (z.B. Lebensalter > Rentenbeginn)
    const validationError = ErrorHandler.validateParameters(steuerParameter);
    if (validationError) {
      onSimulationStateUpdate({
        simulationError: validationError,
        isSimulating: false,
        isRunning1TopfSimulation: false,
      });
      return;
    }

    // Ladezustand sofort setzen, damit der Button während des Laufs
    // deaktiviert ist und Doppelklicks keine parallelen Läufe starten
    onSimulationStateUpdate({
      isSimulating: true,
      isRunning1TopfSimulation: true,
      simulationError: null,
      oneTopfSimulationError: null,
    });

    try {
      // Clear master seeds and all caches once for both simulations to share the same fresh crypto-based randomization
      // All services now imported statically at the top of the file

      RateCalculationService.clearMasterSeeds();
      SharedRateGenerator.clearStaticCache();
      garchSequenceCache.clear();

      // CRITICAL FIX: Generate shared rates ONCE and use for BOTH simulations
      // This ensures identical investment returns for fair strategy comparison
      const maxYears = SharedRateGenerator.calculateMaxYears(steuerParameter);
      const sharedRates = await SharedRateGenerator.generateSharedRates(maxYears, steuerParameter, {
        inflationOption,
        investmentReturnOption,
        tagesgeldOption,
        zinstopfOption,
      });

      // Create engines with identical shared rates
      const threeTopfEngine = new ThreeTopfSimulationEngine(steuerParameter, {
        inflationOption,
        investmentReturnOption,
        tagesgeldOption,
        zinstopfOption,
      }, crisisDefinition as CrisisDefinitionType);

      const oneTopfEngine = new OneTopfSimulationEngine(steuerParameter, {
        inflationOption,
        investmentReturnOption,
      }, crisisDefinition as CrisisDefinitionType);

      // Both engines MUST use the SAME shared rates for fair comparison
      threeTopfEngine.setSharedRates({
        inflationRates: sharedRates.inflationRates,
        verbrauchstopfRates: sharedRates.verbrauchstopfRates,
        investitionstopfRates: sharedRates.investitionstopfRates,
        zinstopfRates: sharedRates.zinstopfRates,
      });

      oneTopfEngine.setSharedRates(sharedRates);

      // Clear remaining caches to ensure fresh simulation
      threeTopfEngine.clearLocalCaches();
      oneTopfEngine.clearLocalCaches();

      // Run both simulations with identical market conditions
      const [threeTopfResult, oneTopfResult] = await Promise.all([
        threeTopfEngine.runSimulation(),
        oneTopfEngine.runSimulation()
      ]);

      // VERIFICATION: Ensure both strategies use identical investment returns
      const threeTopfRates = threeTopfResult.map((year: any) => year.investitionstopfRendite);
      const oneTopfRates = oneTopfResult.map((year: any) => year.investitionstopfRendite);

      // Investment returns comparison verification

      // Check if rates are identical
      const ratesIdentical = threeTopfRates.every((rate: number, index: number) =>
        Math.abs(rate - oneTopfRates[index]) < 0.0001
      );

      if (ratesIdentical) {
        // Both strategies use identical investment returns
      } else {
        // ERROR: Investment returns differ between strategies
        // Calculate differences found at indices for debugging if needed
        // const differences = threeTopfRates.map((rate: number, index: number) =>
        //   Math.abs(rate - oneTopfRates[index]) >= 0.0001 ? index : -1
        // ).filter((idx: number) => idx !== -1);
        // Investment returns differ between strategies (commented for production)
      }

      onSimulationStateUpdate({
        simulationData: threeTopfResult,
        oneTopfSimulationData: oneTopfResult,
        isSimulating: false,
        isRunning1TopfSimulation: false,
      });
    } catch (error) {
      console.error('Error in runAllSimulations:', error);
      onSimulationStateUpdate({
        simulationError: error as any,
      });
    } finally {
      // Ensure loading states are always reset, even on unexpected errors
      onSimulationStateUpdate({
        isSimulating: false,
        isRunning1TopfSimulation: false,
      });
    }
  };

  const clearErrors = () => {
    onSimulationStateUpdate({
      simulationError: null,
      oneTopfSimulationError: null,
      monteCarloError: null,
    });
  };

  // Helper function to create export configuration
  const createExportConfig = (): ExportConfig => ({
    steuerParameter: steuerParameter!,
    simulationOptions,
    crisisDefinition,
    investmentReturnOption,
    inflationOption,
    tagesgeldOption,
    zinstopfOption,
  });

  // JSON Export handlers
  const handleExport3Topf = () => {
    if (!simulationData || simulationData.length === 0 || !steuerParameter) {return;}

    try {
      const exportData = DataExportService.generate3TopfExportData(simulationData, createExportConfig());

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `3-topf-einzelsimulation-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating 3-Topf JSON export:', error);
    }
  };

  // Unified Excel Export handler
  const handleUnifiedExcelExport = async () => {
    if (!simulationData || simulationData.length === 0 || 
        !oneTopfSimulationData || oneTopfSimulationData.length === 0 || 
        !steuerParameter) {
      return;
    }

    try {
      const threeTopfExportData = DataExportService.generate3TopfExportData(simulationData, createExportConfig());
      const oneTopfExportData = DataExportService.generate1TopfExportData(oneTopfSimulationData, createExportConfig());
      
      const formatOptions: ExcelFormattingOptions = {
        separateTransactionSheet: false,
        includeValidationFormulas: false,
        freezeHeaders: true,
        highlightCrisisYears: false,
        highlightNormalYears: false,
        redTextForNegativeReturns: true,
        includeCrossReferences: false,
      };

      const workbook = await FormattedExcelExportService.createUnifiedFormattedWorkbook(
        threeTopfExportData, 
        oneTopfExportData, 
        formatOptions
      );
      await FormattedExcelExportService.downloadFormattedExcelFile(
        workbook, 
        `portfolio-simulation-${new Date().toISOString().split('T')[0]}`
      );
    } catch (error) {
      console.error('Error generating unified Excel export:', error);
    }
  };

  const handleExport1Topf = () => {
    if (!oneTopfSimulationData || oneTopfSimulationData.length === 0 || !steuerParameter) {return;}

    try {
      const exportData = DataExportService.generate1TopfExportData(oneTopfSimulationData, createExportConfig());

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `1-topf-einzelsimulation-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating 1-Topf JSON export:', error);
    }
  };

  // Show validation error if parameters are missing
  if (!steuerParameter) {
    return (
      <Container
        header={
          <Header
            variant="h2"
            description="Führen Sie Einzelsimulationen für Ihre 3-Topf und 1-Topf Strategien durch."
          >
            Einzel-Simulation
          </Header>
        }
      >
        <Alert type="warning" header="Keine Parameter verfügbar">
          Bitte geben Sie zunächst die Berechnungsparameter in "Schritt 1: Individuelle-Parameter" ein, um
          Simulationen durchführen zu können.
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      header={
        <Box>
          <ColumnLayout columns={2}>
            <Header
              variant="h2"
              description="Simulieren Sie die Entwicklung Ihrer 3-Topf und 1-Topf Strategien über die Zeit."
            >
              Einzel-Simulation
            </Header>
            <Box textAlign="right">
              <SpaceBetween direction="horizontal" size="s">
                <Button
                  variant="primary"
                  onClick={runAllSimulations}
                  loading={isSimulating || isRunning1TopfSimulation}
                  disabled={isSimulating || isRunning1TopfSimulation}
                >
                  {isSimulating || isRunning1TopfSimulation
                    ? 'Simulation läuft...'
                    : 'Einzelsimulationen starten'}
                </Button>

                <Button
                  onClick={() => handleUnifiedExcelExport()}
                  iconName="download"
                  disabled={!simulationData || simulationData.length === 0 || !oneTopfSimulationData || oneTopfSimulationData.length === 0}
                  variant="normal"
                >
                  Excel Export (3-Topf & 1-Topf)
                </Button>

                <Button
                  onClick={() => handleExport3Topf()}
                  iconName="download"
                  disabled={!simulationData || simulationData.length === 0}
                  variant="normal"
                >
                  3-Topf JSON Export
                </Button>

                <Button
                  onClick={() => handleExport1Topf()}
                  iconName="download"
                  disabled={!oneTopfSimulationData || oneTopfSimulationData.length === 0}
                  variant="normal"
                >
                  1-Topf JSON Export
                </Button>
              </SpaceBetween>
            </Box>
          </ColumnLayout>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Global Error Display */}
        {(simulationError || oneTopfSimulationError) && (
          <SpaceBetween direction="vertical" size="s">
            {simulationError && (
              <Alert
                type="error"
                header="3-Topf Simulationsfehler"
                dismissible
                onDismiss={clearErrors}
              >
                {simulationError.message}
              </Alert>
            )}

            {oneTopfSimulationError && (
              <Alert
                type="error"
                header="1-Topf Simulationsfehler"
                dismissible
                onDismiss={clearErrors}
              >
                {oneTopfSimulationError.message}
              </Alert>
            )}
          </SpaceBetween>
        )}

        {/* Simulation Controls */}
        <SimpleSimulationControls
          steuerParameter={steuerParameter}
          crisisDefinition={crisisDefinition}
          inflationOption={inflationOption}
          investmentReturnOption={investmentReturnOption}
          tagesgeldOption={tagesgeldOption}
          zinstopfOption={zinstopfOption}
          onCrisisDefinitionChange={setCrisisDefinition}
          onInflationOptionChange={setInflationOption}
          onInvestmentReturnOptionChange={setInvestmentReturnOption}
          onTagesgeldOptionChange={setTagesgeldOption}
          onZinstopfOptionChange={setZinstopfOption}
        />

        {/* Unified Results */}
        <EinzelSimulationTab
          simulationData={simulationData}
          oneTopfSimulationData={oneTopfSimulationData}
          steuerParameter={steuerParameter}
          simulationOptions={{
            inflationOption,
            investmentReturnOption,
            tagesgeldOption,
            zinstopfOption,
          }}
          oneTopfSimulationOptions={{
            inflationOption,
            investmentReturnOption,
          }}
          crisisDefinition={crisisDefinition}
        />
      </SpaceBetween>
    </Container>
  );
};

export default Schritt3;
